import moment from "moment";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { transactionSearchManager } from "../../../../../shared/core/service/services";
import { TimeRange } from "../../../../../shared/domains/accounting-transaction/search/timerange";
import { JustificationFilter } from "../../../../../shared/domains/accounting-transaction/search/transaction-search-manager";
import { useObservable } from "../../../../../shared/utils/observable";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { BluePrimaryButton } from "../../../common/buttons/primary-button";
import { CloseIcon } from "../../../common/close-icon";
import { DateInput } from "../../../common/forms/date-input";
import { theme } from "../../../styles/theme";

/** The ceiling of the spending slider, as the design sets it. */
const AMOUNT_MAX = 3000;

/** The slider moves by the same increment as the app's. */
const AMOUNT_STEP = 10;

/**
 * The range is shared with whoever else sets it, and a slider cannot show what sits outside its
 * own track: a bound from elsewhere is brought back inside before it is read out.
 */
const withinBounds = (amount: number) => Math.min(Math.max(amount, 0), AMOUNT_MAX);

interface TransactionsFilterDrawerProps {
  currencyId: string | undefined;
  toJustifyCount: number;
  toQualifyCount: number;
  onClose: () => void;
}

/**
 * The filter panel of the operation history. It holds its own draft of every filter and hands the
 * lot over on "Appliquer", so moving a slider does not fire a request per step.
 */
export function TransactionsFilterDrawer(props: Readonly<TransactionsFilterDrawerProps>) {
  const { currencyId, toJustifyCount, toQualifyCount, onClose } = props;
  const { formatMessage, formatNumber } = useIntl();

  /**
   * Read out the way the app's slider does. The account carries two currency fields and only the
   * ISO alpha one ("EUR") is a currency: the other is the numeric code, which would print as 978.
   */
  const formatAmount = (amount: number) =>
    currencyId
      ? formatNumber(amount, { currency: currencyId, style: "currency", minimumFractionDigits: 0 })
      : formatNumber(amount);

  const justification = useObservable(transactionSearchManager.justificationSelection);
  const amountRange = useObservable(transactionSearchManager.amountRange);
  const timeRange = useObservable(transactionSearchManager.timeRangeSelection);

  const [draftJustification, setDraftJustification] = useState(justification);
  const [low, setLow] = useState(withinBounds(amountRange[0] ?? 0));
  const [high, setHigh] = useState(withinBounds(amountRange[1] ?? AMOUNT_MAX));
  const [from, setFrom] = useState(toInputDate(timeRange.bounds[0]));
  const [to, setTo] = useState(toInputDate(timeRange.bounds[1]));

  useEffect(() => {
    setDraftJustification(justification);
    setLow(withinBounds(amountRange[0] ?? 0));
    setHigh(withinBounds(amountRange[1] ?? AMOUNT_MAX));
    setFrom(toInputDate(timeRange.bounds[0]));
    setTo(toInputDate(timeRange.bounds[1]));
  }, [justification, amountRange, timeRange]);

  const clearAll = () => {
    setDraftJustification(JustificationFilter.ALL);
    setLow(0);
    setHigh(AMOUNT_MAX);
    setFrom("");
    setTo("");
  };

  const apply = () => {
    transactionSearchManager.setJustificationFilter(draftJustification);
    // Untouched ends mean no bound at all, so the API is not handed a range nobody asked for.
    transactionSearchManager.setAmountRange(low > 0 ? low : null, high < AMOUNT_MAX ? high : null);
    if (from && to) {
      transactionSearchManager.setTimeRange(TimeRange.Other, [moment(from), moment(to)]);
    } else {
      transactionSearchManager.setTimeRange(TimeRange.Quarter);
    }
    onClose();
  };

  const choices = [
    { value: JustificationFilter.ALL, label: formatMessage("transactionFilter.all"), count: undefined },
    {
      value: JustificationFilter.TO_JUSTIFY,
      label: formatMessage("transactionFilter.toJustify"),
      count: toJustifyCount,
    },
    {
      value: JustificationFilter.TO_QUALIFY,
      label: formatMessage("transactionFilter.toQualify"),
      count: toQualifyCount,
    },
  ];

  return (
    <Panel>
      <CloseButton type="button" onClick={onClose} aria-label={formatMessage("transactionFilter.panelTitle")}>
        <CloseIcon />
      </CloseButton>
      <TitleRow>
        <PanelTitle>{formatMessage("transactionFilter.panelTitle")}</PanelTitle>
        <ClearLink type="button" onClick={clearAll}>
          {formatMessage("transactionFilter.clearAll")}
        </ClearLink>
      </TitleRow>

      <Choices>
        {choices.map((choice) => (
          <Choice
            key={choice.value}
            type="button"
            $selected={draftJustification === choice.value}
            onClick={() => setDraftJustification(choice.value)}
          >
            {choice.label}
            {choice.count !== undefined && choice.count > 0 && <Count>{choice.count}</Count>}
          </Choice>
        ))}
      </Choices>

      <SectionTitle>{formatMessage("transactionFilter.amountRangeTitle")}</SectionTitle>
      <RangeBounds>
        <span>{formatAmount(low)}</span>
        <span>{formatAmount(high)}</span>
      </RangeBounds>
      {/* Two sliders on the same track: one thumb each, neither allowed past the other. */}
      <RangeTrack>
        <RangeFill $from={(low / AMOUNT_MAX) * 100} $to={(high / AMOUNT_MAX) * 100} />
        <RangeInput
          type="range"
          min={0}
          max={AMOUNT_MAX}
          step={AMOUNT_STEP}
          value={low}
          aria-label={formatMessage("transactionFilter.amountRange.min")}
          onChange={(e) => setLow(Math.min(Number(e.target.value), high))}
        />
        <RangeInput
          type="range"
          min={0}
          max={AMOUNT_MAX}
          step={AMOUNT_STEP}
          value={high}
          aria-label={formatMessage("transactionFilter.amountRange.max")}
          onChange={(e) => setHigh(Math.max(Number(e.target.value), low))}
        />
      </RangeTrack>

      <SectionTitle>{formatMessage("transactionFilter.periodTitle")}</SectionTitle>
      <Dates>
        <DateField>
          <DateCaption>{formatMessage("transactionFilter.timeRange.from")}</DateCaption>
          <StyledDateInput mode="date" withoutTheme value={from} onChange={(e) => setFrom(e.target.value)} />
        </DateField>
        <DateField>
          <DateCaption>{formatMessage("transactionFilter.timeRange.to")}</DateCaption>
          <StyledDateInput mode="date" withoutTheme value={to} onChange={(e) => setTo(e.target.value)} />
        </DateField>
      </Dates>

      <ApplyButton size="XS" onClick={apply}>
        {formatMessage("transactionFilter.apply")}
      </ApplyButton>
    </Panel>
  );
}

function toInputDate(bound: moment.Moment | null) {
  return bound ? bound.format("YYYY-MM-DD") : "";
}

const Panel = styled.aside`
  position: fixed;
  z-index: 150;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 370px;
  max-width: 100%;
  padding: 24px;
  background-color: #ffffff;
  box-shadow: -8px 0 24px 0 rgba(0, 0, 0, 0.08);
  overflow-y: auto;
`;

const CloseButton = styled(UnstyledButton)`
  align-self: flex-end;
  padding: 8px;
  margin: -8px -8px 0 0;
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 16px;
`;

const PanelTitle = styled.h2`
  ${theme.mediumText};
  margin: 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const ClearLink = styled(UnstyledButton)`
  ${theme.text};
  font-size: 14px;
  color: ${theme.colors.blue[700]};
`;

const Choices = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
`;

const Choice = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid ${(props) => (props.$selected ? theme.colors.blue[700] : theme.colors.gray[300])};
  border-radius: 20px;
  background-color: #ffffff;
  cursor: pointer;
  font-size: 14px;
  color: ${(props) => (props.$selected ? theme.colors.blue[700] : theme.colors.blue[800])};
`;

const Count = styled.span`
  font-size: 12px;
  color: ${theme.colors.gray[700]};
`;

const SectionTitle = styled.h3`
  ${theme.text};
  margin: 28px 0 12px;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.blue[800]};
`;

const RangeBounds = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${theme.text};
  font-size: 13px;
  color: ${theme.colors.blue[800]};
`;

const RangeTrack = styled.div`
  position: relative;
  height: 24px;
  margin-top: 8px;

  &::before {
    content: "";
    position: absolute;
    top: 11px;
    right: 0;
    left: 0;
    height: 2px;
    border-radius: 1px;
    background-color: ${theme.colors.gray[300]};
  }
`;

const RangeFill = styled.div<{ $from: number; $to: number }>`
  position: absolute;
  top: 11px;
  left: ${(props) => props.$from}%;
  width: ${(props) => props.$to - props.$from}%;
  height: 2px;
  border-radius: 1px;
  background-color: ${theme.colors.blue[700]};
`;

/* Both inputs cover the track; only their thumbs take the pointer, so either end stays reachable. */
const RangeInput = styled.input`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 24px;
  margin: 0;
  appearance: none;
  background: transparent;
  pointer-events: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 2px solid #ffffff;
    border-radius: 50%;
    background-color: ${theme.colors.blue[700]};
    cursor: pointer;
    pointer-events: auto;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: 2px solid #ffffff;
    border-radius: 50%;
    background-color: ${theme.colors.blue[700]};
    cursor: pointer;
    pointer-events: auto;
  }
`;

const Dates = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const DateField = styled.label`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
`;

const DateCaption = styled.span`
  ${theme.text};
  font-size: 12px;
  color: ${theme.colors.gray[800]};
`;

const StyledDateInput = styled(DateInput)`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  color: ${theme.colors.blue[800]};
`;

const ApplyButton = styled(BluePrimaryButton)`
  width: 100%;
  margin-top: 28px;
  padding: 13px;
  box-shadow: none;
`;
